<template>
  <div class="subscribe-page">
    <div class="subscribe-plan">
      <header class="subscribe-top">
        <router-link
          :to="{ name: 'home' }"
          class="subscribe-logo"
          aria-label="Sonata home"
        >
          <img
            src="@/assets/sonata-logo.svg"
            width="112"
            height="30"
            alt="Sonata"
          >
        </router-link>
      </header>

      <div class="subscribe-plan-head">
        <span class="subscribe-eyebrow">{{ trialMode ? `${trialDays}-day free trial on every plan` : 'Pricing' }}</span>
        <h1>Choose your plan</h1>
        <p class="subscribe-lede">
          <template v-if="trialMode">
            Pick the licence that covers where your videos will go. You won't pay anything for {{ trialDays }} days, and you can switch plans or cancel at any time.
          </template>
          <template v-else>
            Pick the licence that covers where your videos will go. Every plan includes unlimited downloads, and you can switch plans or cancel at any time.
          </template>
        </p>
        <div
          class="subscribe-period"
          role="group"
          aria-label="Billing period"
        >
          <button
            type="button"
            :aria-pressed="interval === 'month' ? 'true' : 'false'"
            @click="interval = 'month'"
          >
            Monthly
          </button>
          <button
            type="button"
            :aria-pressed="interval === 'year' ? 'true' : 'false'"
            @click="interval = 'year'"
          >
            Yearly
            <span
              v-if="monthsFree"
              class="subscribe-save"
            >{{ monthsFree }} {{ monthsFree === 1 ? 'month' : 'months' }} free</span>
          </button>
        </div>
      </div>

      <p
        v-if="cards.length === 0"
        class="subscribe-loading"
      >
        Loading plans…
      </p>
      <div
        v-else
        class="subscribe-cards"
      >
        <article
          v-for="card in cards"
          :key="card.slug"
          class="subscribe-card"
        >
          <div class="subscribe-card-top">
            <img
              v-if="card.icon"
              :src="card.icon"
              width="48"
              height="48"
              alt=""
            >
            <h2 class="title-wide">
              {{ card.name }}
            </h2>
            <p>{{ card.description }}</p>
          </div>
          <div class="subscribe-price">
            <span class="subscribe-price-big">{{ card.perMonth }} <small>/ month</small></span>
            <span class="subscribe-price-sub">{{ card.billing }}</span>
          </div>
          <div class="subscribe-card-action">
            <button
              type="button"
              class="subscribe-button"
              @click="choose(card.plan)"
            >
              {{ trialMode ? 'Start free trial' : `Choose ${card.name}` }}
            </button>
          </div>
          <ul class="subscribe-features">
            <li
              v-for="feature in card.features"
              :key="feature"
            >
              {{ feature }}
            </li>
          </ul>
        </article>
      </div>

      <div class="subscribe-plan-foot">
        <p>Only need one track? <router-link :to="browseMusicRoute">License a single track instead</router-link>.</p>
        <p>
          Questions about which plan is right?
          <router-link :to="{ name: 'faq' }">Read our FAQ</router-link>
          or email us at <a href="mailto:hello@sonata.media">hello@sonata.media</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { browseMusicRoute } from '@/utils/catalogue.js'
import { TRIAL_DAYS, formatPrice, groupPlans, monthlyAmount, monthsFreeYearly, planIcon, yearlySaving } from '@/utils/subscribeFlow.js'

export default {
  name: 'SubscribePlan',

  data () {
    return {
      interval: 'year',
      trialDays: TRIAL_DAYS
    }
  },

  metaInfo: {
    title: 'Choose your plan'
  },

  computed: {
    ...mapGetters({
      plans: 'subscriptions/plans',
      trialMode: 'subscriptions/createSubscriptionWithTrial',
      currentTeam: 'user/currentTeam'
    }),

    groups: function () {
      return groupPlans(this.plans)
    },

    monthsFree: function () {
      return Math.max(0, ...this.groups.map(monthsFreeYearly))
    },

    cards: function () {
      return this.groups
        .filter((group) => group[this.interval])
        .map((group) => {
          const plan = group[this.interval]
          const saving = yearlySaving(group)
          let billing = 'Billed monthly'
          if (this.interval === 'year') {
            billing = `${formatPrice(plan.amount, plan.currency)} billed yearly`
            if (saving) {
              billing += `, save ${formatPrice(saving, plan.currency)}`
            }
          }
          return {
            ...group,
            plan,
            icon: planIcon(group.slug),
            perMonth: formatPrice(monthlyAmount(plan), plan.currency),
            billing
          }
        })
    },

    browseMusicRoute: function () {
      return browseMusicRoute()
    }
  },

  watch: {
    // Anyone with a subscription already manages it on their subscription page.
    currentTeam: {
      immediate: true,
      handler (team) {
        if (team && team.current_subscription) {
          this.$router.replace({ name: 'subscription' })
        }
      }
    }
  },

  methods: {
    choose (plan) {
      const query = { plan: String(plan.pk) }
      if (this.$route.query.next) {
        query.next = this.$route.query.next
      }
      this.$router.push({ name: 'subscribe-payment', query })
    }
  }
}
</script>

<style lang="scss">
@import "../assets/scss/_variables.scss";
@import "../assets/scss/_mixins.scss";

// Shared by the subscribe flow's pages; everything is namespaced under
// .subscribe-page.
.subscribe-page {
  // Cancel #app's padding, which leaves room for the header and player.
  margin: -120px 0 -160px;
  min-height: 100vh;
  background: $white;
  color: $bluegrey3;
  font-size: 14px;

  h1 {
    margin: 0;
    color: $bluegrey1;
    font-weight: 500;
    font-size: 44px;
    line-height: 1.1;
    letter-spacing: -0.01em;
  }

  a {
    color: $bluegrey1;
  }

  a:focus-visible,
  button:focus-visible,
  input:focus-visible,
  select:focus-visible {
    outline: 2px solid $bluegrey1;
    outline-offset: 2px;
  }

  .subscribe-logo {
    display: block;
    width: 112px;
    img {
      display: block;
    }
  }

  .subscribe-eyebrow {
    @include uppercase();
    font-size: 12px;
    font-weight: 400 !important;
    color: $bluegrey3;
  }

  .subscribe-lede {
    margin: 0;
    max-width: 60ch;
    font-size: 16px;
    line-height: 1.5;
    color: $bluegrey2;
  }

  .subscribe-save {
    padding: 3px 8px;
    border-radius: 999px;
    background: #fdf1da;
    color: rgb(122, 78, 6);
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
  }

  .subscribe-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 50px;
    padding: 0 24px;
    border: 0;
    border-radius: 999px;
    background: $accent;
    color: $white;
    font: 500 15px $family-sans-serif;
    cursor: pointer;
    transition: background 0.12s ease-out;
    &:hover {
      background: darken($accent, 6%);
    }
    &[disabled] {
      opacity: 0.6;
      cursor: progress;
    }
  }

  .subscribe-features {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 24px 0 0;
    padding: 20px 0 0;
    border-top: 1px solid $bluegrey5;
    list-style: none;
    li {
      display: flex;
      gap: 10px;
      color: $bluegrey2;
      font-size: 14px;
      line-height: 1.4;
      &::before {
        content: "";
        flex: none;
        width: 16px;
        height: 16px;
        margin-top: 1px;
        background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M3 8.3l3.2 3.2L13 4.7' fill='none' stroke='%23142136' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / 16px no-repeat;
      }
    }
  }

  @media (max-width: 860px) {
    h1 {
      font-size: 36px;
    }
  }
}

.subscribe-plan {
  max-width: 1152px;
  margin: 0 auto;
  padding: 0 16px 72px;

  .subscribe-top {
    padding: 20px 0 8px;
  }

  .subscribe-plan-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding-bottom: 28px;
    text-align: center;
  }

  .subscribe-period {
    display: inline-flex;
    margin-top: 4px;
    padding: 4px;
    border-radius: 999px;
    background: $bluegrey7;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.08);
    button {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 40px;
      padding: 0 20px;
      border: 0;
      border-radius: 999px;
      background: none;
      color: $bluegrey3;
      font: 500 14px $family-sans-serif;
      cursor: pointer;
      &[aria-pressed="true"] {
        background: $white;
        color: $bluegrey1;
        box-shadow: 0 1px 3px rgba(6, 11, 18, 0.15);
      }
    }
  }

  .subscribe-loading {
    text-align: center;
  }

  .subscribe-cards {
    display: grid;
    gap: 16px;
  }

  .subscribe-card {
    display: grid;
    align-content: start;
    padding: 28px 24px;
    border-radius: 4px;
    background: $bluegrey7;
  }

  .subscribe-card-top {
    display: flex;
    flex-direction: column;
    gap: 12px;
    h2 {
      margin: 4px 0 0;
      color: $bluegrey1;
      font-size: 18px;
    }
    p {
      margin: 0;
      color: $bluegrey1;
      font-size: 15px;
      line-height: 1.45;
    }
  }

  .subscribe-price {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 24px;
  }

  .subscribe-price-big {
    color: $bluegrey1;
    font-size: 32px;
    font-weight: 500;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    small {
      color: $bluegrey3;
      font-size: 15px;
      font-weight: 400;
      letter-spacing: 0;
    }
  }

  .subscribe-price-sub {
    min-height: 1.4em;
    font-size: 13px;
    font-variant-numeric: tabular-nums;
  }

  .subscribe-card-action {
    padding-top: 20px;
  }

  .subscribe-plan-foot {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding-top: 32px;
    font-size: 14px;
    text-align: center;
    p {
      margin: 0;
    }
  }

  // Side by side, with each card's rows lined up across the three.
  @media (min-width: 960px) {
    .subscribe-cards {
      grid-auto-columns: minmax(0, 1fr);
      grid-auto-flow: column;
      grid-template-rows: repeat(4, auto);
    }
    .subscribe-card {
      grid-row: span 4;
      grid-template-rows: subgrid;
    }
  }
}
</style>
